import { getOrCreateSessionId } from '../../utils/session';

const STUB_PATH = '/afd-captcha-stub';

/**
 * Builds the full CAPTCHA stub URL, including the `parentOrigin`, `source`, and `sessionId`
 * query parameters. The stub is loaded as an iframe `src`, so it cannot send request headers -
 * the session id is therefore passed as a query parameter so backend logs for the stub request
 * correlate with the rest of the user's session.
 *
 * @param backendUrl - Backend origin (e.g. env.REACT_APP_BACKEND_URL).
 * @param source - Page identifier for logging (e.g. 'rac-form', 'rac-bing-search-suggestion', 'appeals-account').
 * @returns Absolute URL to the CAPTCHA stub endpoint with query parameters appended.
 */
export function getStubUrl(backendUrl: string, source?: string): string {
    const base = `${backendUrl.replace(/\/$/, '')}${STUB_PATH}`;
    const separator = base.includes('?') ? '&' : '?';
    let url = `${base}${separator}parentOrigin=${encodeURIComponent(window.location.origin)}`;
    if (source) {
        url += `&source=${encodeURIComponent(source)}`;
    }

    try {
        url += `&sessionId=${encodeURIComponent(getOrCreateSessionId())}`;
    } catch {
        // session id is best-effort; never block stub loading if it cannot be produced.
    }

    return url;
}

/**
 * Returns the list of origins accepted as legitimate senders of the CAPTCHA postMessage.
 * Includes the current window origin and the stub iframe origin (if different).
 *
 * @param backendUrl - Backend origin.
 * @returns Array of accepted origin strings.
 */
export function getAcceptedOrigins(backendUrl: string): string[] {
    const origins = [window.location.origin];
    try {
        const stubOrigin = new URL(getStubUrl(backendUrl)).origin;
        if (!origins.includes(stubOrigin)) origins.push(stubOrigin);
    } catch {
        // relative URL — same origin, already included
    }
    return origins;
}
